/**
 * Base Transport - abstract class for all transport implementations
 *
 * Provides common functionality:
 * - Status management and change events
 * - Error tracking and handling
 * - Event handler management
 * - Reconnection state management
 */

import type { ITransport, TransportConfig, TransportStatus } from '../types';
import { createModuleLogger } from '../utils';

export abstract class BaseTransport implements ITransport {
  readonly id: string;

  readonly type: ITransport['type'];

  protected config: TransportConfig;

  private _status: TransportStatus = 'disconnected';

  private _lastError?: Error;

  protected readonly logger: ReturnType<typeof createModuleLogger>;

  get status(): TransportStatus {
    return this._status;
  }

  get lastError(): Error | undefined {
    return this._lastError;
  }

  private statusChangeHandlers: Set<(status: TransportStatus) => void> = new Set();

  private messageHandlers: Set<(raw: string | ArrayBuffer) => void> = new Set();

  private errorHandlers: Set<(error: Error) => void> = new Set();

  constructor(id: string, type: ITransport['type'], config: TransportConfig) {
    this.id = id;
    this.type = type;
    this.config = config;
    this.logger = createModuleLogger(`Transport[${this.type}]`);
  }

  /**
   * Get last error
   */
  get getLastError(): Error | undefined {
    return this.lastError;
  }

  /**
   * Abstract method - subclasses must implement
   */
  abstract connect(): Promise<void>;

  /**
   * Abstract method - subclasses must implement
   */
  abstract disconnect(): Promise<void>;

  /**
   * Abstract method - subclasses must implement
   */
  abstract send(data: string | ArrayBuffer): Promise<void>;

  /**
   * Register message handler
   * @param handler - Called with raw bytes when message received
   * @returns Unsubscribe function
   */
  onMessage(handler: (raw: string | ArrayBuffer) => void): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Register status change handler
   * @param handler - Called when status changes
   * @returns Unsubscribe function
   */
  onStatusChange(handler: (status: TransportStatus) => void): () => void {
    this.statusChangeHandlers.add(handler);
    return () => {
      this.statusChangeHandlers.delete(handler);
    };
  }

  /**
   * Register error handler
   * @param handler - Called when error occurs
   * @returns Unsubscribe function
   */
  onError(handler: (error: Error) => void): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  /**
   * Protected helper - emit message to all handlers
   */
  protected emitMessage(raw: string | ArrayBuffer): void {
    for (const handler of this.messageHandlers) {
      try {
        handler(raw);
      } catch (error) {
        this.logger.error('Message handler error', error as Error);
      }
    }
  }

  /**
   * Protected helper - update status and notify handlers
   */
  protected setStatus(newStatus: TransportStatus): void {
    if (this._status !== newStatus) {
      this._status = newStatus;
      this.logger.debug(`Status changed to ${newStatus}`);
      for (const handler of this.statusChangeHandlers) {
        try {
          handler(newStatus);
        } catch (error) {
          this.logger.error('Status handler error', error as Error);
        }
      }
    }
  }

  /**
   * Protected helper - record and emit error
   */
  protected emitError(error: Error): void {
    this._lastError = error;
    this.logger.error('Transport error', error);
    for (const handler of this.errorHandlers) {
      try {
        handler(error);
      } catch (err) {
        this.logger.error('Error handler failed', err as Error);
      }
    }
  }

  /**
   * Protected helper - cleanup handlers
   */
  protected cleanup(): void {
    this.messageHandlers.clear();
    this.statusChangeHandlers.clear();
    this.errorHandlers.clear();
  }
}
